import { Dispatch } from 'react'
import moment from 'moment'
import { Submission } from '@global/types'

export interface SubmittedBy {
  value: number
  label: string
}

export const Order = {
  ASC: 'asc',
  DESC: 'desc',
} as const
export type Order = (typeof Order)[keyof typeof Order]

export const OrderBy = {
  TEMPLATE_NAME: 'template_name',
  SUBMISSION_NAME: 'submission_name',
  CREATED_BY: 'created_by',
  CREATED_AT: 'created_at',
} as const
export type OrderBy = (typeof OrderBy)[keyof typeof OrderBy]

export interface SortOptions {
  order: Order
  orderBy: OrderBy
  offset: number
}

export interface ContextState {
  isLoading: boolean
  page: number
  requestsBySubmitter: Submission[] | null
  requestsWithinDateRange: Submission[]
  reviewingRequest: Submission | null
  submittedBy: SubmittedBy
  submittedByOptions: SubmittedBy[]
  submittedFrom: moment.Moment | null
  submittedTo: moment.Moment | null
  reloadApprovalRequests: boolean
  sortOptions: SortOptions
}

export interface ContextValues extends ContextState {
  approvalRequests: Submission[] | null
  filtersHaveChanged: boolean
  dispatch: Dispatch<any>
  handleResetFilters: () => void
  updateApprovalRequests: ({
    orderBy,
    order,
    offset,
  }: {
    orderBy: OrderBy
    order: Order
    offset: number
  }) => Promise<Submission[]>
  showSpinner: (cb: () => void) => NodeJS.Timeout
  showApprovalRequestsTable: () => void
  showReviewingApprovalRequest: (datasetId: number) => void
}

export enum ActionType {
  handleUpdateSortBy = 'handleUpdateSortBy',
  handleUpdateSubmittedBy = 'handleUpdateSubmittedBy',
  handleUpdateSubmittedFrom = 'handleUpdateSubmittedFrom',
  handleUpdateSubmittedTo = 'handleUpdateSubmittedTo',
  resetAllFilters = 'resetAllFilters',
  setApprovalRequests = 'setApprovalRequests',
  setIsLoading = 'setIsLoading',
  setPage = 'setPage',
  setReviewingRequest = 'setReviewingRequest',
  setSubmittedByOptions = 'setSubmittedByOptions',
  setSortOptions = 'setSortOptions',
}

export type Action =
  | {
      type: ActionType.handleUpdateSortBy
      payload: {
        page: number
        requestsOnCurrentPage: Submission[]
        requestsWithinDateRange: Submission[]
      }
    }
  | {
      type: ActionType.handleUpdateSubmittedBy
      payload: {
        submittedBy: SubmittedBy
        requestsBySubmitter: Submission[]
      }
    }
  | {
      type: ActionType.handleUpdateSubmittedFrom
      payload: {
        submittedFrom: moment.Moment
        requestsWithinDateRange: Submission[]
        submittedByOptions: SubmittedBy[]
      }
    }
  | {
      type: ActionType.handleUpdateSubmittedTo
      payload: {
        submittedTo: moment.Moment
        requestsWithinDateRange: Submission[]
        submittedByOptions: SubmittedBy[]
      }
    }
  | {
      type: ActionType.resetAllFilters
      payload: {
        page: number
        submittedBy: SubmittedBy
        submittedFrom: moment.Moment | null
        submittedTo: moment.Moment | null
        requestsBySubmitter: null
        requestsWithinDateRange: Submission[]
        submittedByOptions: SubmittedBy[]
        isLoading: boolean
      }
    }
  | {
      type: ActionType.setApprovalRequests
      payload: {
        requestsWithinDateRange: Submission[]
        submittedByOptions: SubmittedBy[]
        isLoading: boolean
        page: number
        sortOptions: SortOptions
      }
    }
  | { type: ActionType.setIsLoading; payload: boolean }
  | {
      type: ActionType.setPage
      payload: {
        page: number
        sortOptions: SortOptions
      }
    }
  | {
      type: ActionType.setReviewingRequest
      payload: {
        reviewingRequest?: Submission | null
        reloadApprovalRequests?: boolean
      }
    }
  | { type: ActionType.setSubmittedByOptions; payload: SubmittedBy[] }
  | { type: ActionType.setSortOptions; payload: SortOptions }
